import { useSyncExternalStore } from "react";

/**
 * ═══════════════════════════════════════════════════════════════
 *  登录过期的全局状态
 * ═══════════════════════════════════════════════════════════════
 *
 * 老板：「页面提示登录过期应该在页面顶部有一个弹窗提示，
 * 然后点击停留此页和重新登录，当然停留此页就没内容」。
 *
 * 🔴 **「会话过期」和「本来就没登录」是两件事，必须分开。**
 *
 *    - **过期**：他正在用着，中途 token 失效了 —— 这时候**不能把他踢走**。
 *      页面上可能有他填了一半的东西，直接跳走等于把那些东西吃掉。
 *      正确做法是在顶部说一句，让他自己选：接着待着，还是去重新登录。
 *    - **本来就没登录**：直接打开 `/admin` 这种页 —— 那没什么可"停留"的，
 *      整页是空的，跳登录页才是对的。
 *
 *    靠 `useMe()` 返回 null 分不出这两种（两种都是 null）。
 *    所以这里记一个 `everLoggedIn`：**这个页面生命周期里有没有成功登录过**。
 *
 * ⚠️ 用模块级变量 + `useSyncExternalStore`，不放 React state ——
 *    通知它的地方是 `api/client.ts`（一个普通模块，不是组件），
 *    它没法调 setState。
 */

let everLoggedIn = false;
let expired = false;
const listeners = new Set<() => void>();

function emit(): void {
  for (const fn of listeners) fn();
}

/** `useMe` 拿到人就调一次。⚠️ 也顺手清掉过期标记（重新登录成功了）。 */
export function noteLoggedIn(): void {
  const changed = !everLoggedIn || expired;
  everLoggedIn = true;
  expired = false;
  if (changed) emit();
}

/**
 * 续期失败、确认是真的掉线了。
 * ⚠️ **没登录过的人不弹** —— 游客逛商城时 `/auth/me` 本来就 401，
 *    给他弹「登录过期」是凭空吓人。
 */
export function noteExpired(): void {
  if (!everLoggedIn || expired) return;
  expired = true;
  emit();
}

/** 「停留此页」：把提示收掉，别的什么都不做。 */
export function dismissExpired(): void {
  if (!expired) return;
  expired = false;
  emit();
}

/** 退出登录时调 —— 主动退出不是"过期"，下次进来要当新会话。 */
export function resetSessionState(): void {
  everLoggedIn = false;
  expired = false;
  emit();
}

/** 🔴 路由守卫用它决定"该跳登录页"还是"该让顶部提示来处理"。 */
export function everLoggedInThisSession(): boolean {
  return everLoggedIn;
}

export function useSessionExpired(): boolean {
  return useSyncExternalStore(
    (fn) => {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    () => expired,
    () => false,
  );
}
